import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface ImportRow {
  name: string;
  email: string;
  hasConflict: boolean;
  existingEmail?: string;
  invalidEmail?: boolean;
}

interface Props {
  open: boolean;
  rows: ImportRow[];
  conflictResolution: 'update' | 'skip';
  onConflictResolutionChange: (v: 'update' | 'skip') => void;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
}

export function ImportModal({ open, rows, conflictResolution, onConflictResolutionChange, onClose, onConfirm, loading }: Props) {
  const { t } = useTranslation();
  const hasConflicts = rows.some((r) => r.hasConflict);
  const newCount = rows.filter((r) => !r.hasConflict).length;
  const conflictCount = rows.filter((r) => r.hasConflict).length;
  const invalidEmailCount = rows.filter((r) => r.invalidEmail).length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pt: 3, pb: 1.5, px: 3 }}>
        {t('dashboard.training.employee.table.dialogs.import.title', 'Mitarbeiter importieren')}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <p className="text-sm text-gray-500 mb-3">
          {t(
            'dashboard.training.employee.table.dialogs.import.summary',
            '{{new}} neu, {{conflicts}} bereits vorhanden',
            { new: newCount, conflicts: conflictCount }
          )}
        </p>

        <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
          {rows.map((row, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 rounded px-3 py-2 text-sm ${
                row.hasConflict ? 'bg-yellow-50' : 'bg-green-50'
              }`}
            >
              <span className="flex-shrink-0">
                {row.hasConflict ? (
                  <WarningAmberIcon fontSize="small" className="text-yellow-500" />
                ) : (
                  <CheckCircleOutlineIcon fontSize="small" className="text-green-500" />
                )}
              </span>
              <span className="font-medium flex-1 truncate">{row.name}</span>
              <span className={`truncate ${row.invalidEmail ? 'text-red-500 line-through' : 'text-gray-400'}`}>
                {row.email || '—'}
              </span>
              {row.invalidEmail && (
                <span className="text-xs text-red-400 whitespace-nowrap">
                  {t('dashboard.training.employee.table.dialogs.import.invalid_email', 'ungültige E-Mail')}
                </span>
              )}
              {!row.invalidEmail && row.hasConflict && row.existingEmail && row.existingEmail !== row.email && (
                <span className="text-xs text-gray-400 truncate">
                  ({t('dashboard.training.employee.table.dialogs.import.existing_email', 'bisher')}: {row.existingEmail})
                </span>
              )}
            </div>
          ))}
        </div>

        {invalidEmailCount > 0 && (
          <p className="mt-3 text-xs text-red-500">
            {t(
              'dashboard.training.employee.table.dialogs.import.invalid_email_note',
              '{{count}} E-Mail-Adresse(n) sind ungültig und werden nicht importiert.',
              { count: invalidEmailCount }
            )}
          </p>
        )}

        {hasConflicts && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">
              {t('dashboard.training.employee.table.dialogs.import.conflict_resolution_label', 'Was soll mit bereits vorhandenen Mitarbeitern passieren?')}
            </p>
            <RadioGroup
              value={conflictResolution}
              onChange={(e) => onConflictResolutionChange(e.target.value as 'update' | 'skip')}
            >
              <FormControlLabel
                value="update"
                control={<Radio size="small" />}
                label={
                  <span className="text-sm">
                    {t('dashboard.training.employee.table.dialogs.import.conflict_update', 'E-Mail-Adresse aktualisieren')}
                  </span>
                }
              />
              <FormControlLabel
                value="skip"
                control={<Radio size="small" />}
                label={
                  <span className="text-sm">
                    {t('dashboard.training.employee.table.dialogs.import.conflict_skip', 'Überspringen')}
                  </span>
                }
              />
            </RadioGroup>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          {t('components.ui.alert_dialog.actions.cancel', 'Abbrechen')}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={loading || rows.length === 0}
          startIcon={loading ? <CircularProgress size={14} /> : undefined}
        >
          {t('dashboard.training.employee.table.dialogs.import.confirm', 'Importieren')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
